import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_PERMISSIONS, AppPermission } from '../../app-permissions';
import { AuthService, CurrentUser } from '../../auth.service';

interface TokenSummary {
  label: string;
  present: boolean;
  issuedAt: string | null;
  expiresAt: string | null;
  secondsUntilExpiry: number | null;
  userId: number | null;
  error: string | null;
}

interface RouteProbe {
  label: string;
  path: string;
  permissions: AppPermission[];
  allowed: boolean;
}

interface ApiProbeDefinition {
  label: string;
  url: string;
  permissions: AppPermission[];
}

interface ApiProbeResult extends ApiProbeDefinition {
  expectedAllowed: boolean;
  status: number | null;
  ok: boolean;
  bodyPreview: string;
  error: string | null;
}

interface ApiExpectation extends ApiProbeDefinition {
  allowed: boolean;
}

interface InspectableUserOption {
  user_id: number;
  email: string;
  company_name: string | null;
  roles_mask: number;
  roles: string[];
}

@Component({
  selector: 'app-auth-debug',
  templateUrl: './auth-debug.component.html',
  styleUrls: ['./auth-debug.component.scss']
})
export class AuthDebugComponent implements OnInit {
  loading = false;
  sessionUser: CurrentUser | null = null;
  inspectedUser: CurrentUser | null = null;
  inspectedUserJson = '';
  accessTokenSummary: TokenSummary = this.getEmptyTokenSummary('Access Token');
  refreshTokenSummary: TokenSummary = this.getEmptyTokenSummary('Refresh Token');
  routeProbes: RouteProbe[] = [];
  apiExpectations: ApiExpectation[] = [];
  liveApiProbeResults: ApiProbeResult[] = [];
  errorMessage = '';
  lastUpdated: string | null = null;
  canInspectOtherUsers = false;
  inspectableUsers: InspectableUserOption[] = [];
  selectedUserId: number | null = null;

  private readonly routeProbeDefinitions: Array<Omit<RouteProbe, 'allowed'>> = [
    {
      label: 'Users',
      path: '/users',
      permissions: [APP_PERMISSIONS.USERS_READ]
    },
    {
      label: 'Menu Manager',
      path: '/menu_manager',
      permissions: [APP_PERMISSIONS.MENU_MANAGE]
    },
    {
      label: 'Manage Menu Items',
      path: '/manage_menu_items',
      permissions: [APP_PERMISSIONS.MENU_MANAGE]
    },
    {
      label: 'Contact Access Profiles',
      path: '/contact_access_profiles',
      permissions: [APP_PERMISSIONS.ADMIN_TOOLS]
    }
  ];

  private readonly apiProbeDefinitions: ApiProbeDefinition[] = [
    {
      label: 'Current User Context',
      url: '/api/me.php',
      permissions: []
    },
    {
      label: 'Users API',
      url: '/api/users/users.php',
      permissions: [APP_PERMISSIONS.USERS_READ]
    },
    {
      label: 'Menu Manager API',
      url: '/api/menu_manager/menu_manager.php?type=menu',
      permissions: [APP_PERMISSIONS.MENU_MANAGE]
    },
    {
      label: 'Contact Access Profiles API',
      url: '/api/dynamic_grids/contact_access_profiles.php?request_type=profiles_list',
      permissions: [APP_PERMISSIONS.ADMIN_TOOLS]
    }
  ];

  constructor(
    private authService: AuthService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.refreshDebugData();
  }

  refreshDebugData(): void {
    this.loading = true;
    this.errorMessage = '';

    this.authService.loadCurrentUser(true).subscribe({
      next: (currentUser) => {
        this.sessionUser = currentUser;
        this.accessTokenSummary = this.buildTokenSummary('Access Token', localStorage.getItem('access_token'));
        this.refreshTokenSummary = this.buildTokenSummary('Refresh Token', localStorage.getItem('refresh_token'));
        this.canInspectOtherUsers = !!currentUser && currentUser.roles.includes('SUPER_ADMIN');

        this.loadInspectableUsers().subscribe({
          next: (users) => {
            this.inspectableUsers = users;
            this.selectedUserId = this.resolveSelectedUserId(currentUser, users);
            this.loadInspectedContextAndLiveProbes(true);
          },
          error: () => {
            this.errorMessage = 'Failed to load the super admin user list.';
            this.loading = false;
          }
        });
      },
      error: () => {
        this.errorMessage = 'Failed to load the current user authorization context.';
        this.loading = false;
      }
    });
  }

  onInspectedUserChange(): void {
    this.loadInspectedContextAndLiveProbes(false);
  }

  trackByPath(_: number, probe: RouteProbe): string {
    return probe.path;
  }

  trackByUrl(_: number, probe: { url: string }): string {
    return probe.url;
  }

  trackByInspectableUserId(_: number, user: InspectableUserOption): number {
    return user.user_id;
  }

  get selectedInspectableUser(): InspectableUserOption | null {
    if (this.selectedUserId === null) {
      return null;
    }

    return this.inspectableUsers.find((user) => user.user_id === this.selectedUserId) ?? null;
  }

  formatInspectableUserMeta(user: InspectableUserOption): string {
    const details = [`#${user.user_id}`];

    if (user.company_name?.trim()) {
      details.push(user.company_name.trim());
    }

    return details.join(' · ');
  }

  formatInspectableUserRoles(user: InspectableUserOption, limit = 3): string {
    const readableRoles = user.roles.map((role) => this.formatRoleName(role));

    if (readableRoles.length === 0) {
      return 'No roles';
    }

    if (readableRoles.length <= limit) {
      return readableRoles.join(', ');
    }

    return `${readableRoles.slice(0, limit).join(', ')} +${readableRoles.length - limit} more`;
  }

  private formatRoleName(role: string): string {
    return role
      .toLowerCase()
      .split('_')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private loadInspectableUsers(): Observable<InspectableUserOption[]> {
    if (!this.canInspectOtherUsers) {
      return of([]);
    }

    return this.http.get<InspectableUserOption[]>('/api/auth_debug_users.php').pipe(
      catchError(() => of([]))
    );
  }

  private loadInspectedContextAndLiveProbes(includeLiveProbes: boolean): void {
    if (!this.sessionUser) {
      this.inspectedUser = null;
      this.inspectedUserJson = '';
      this.routeProbes = [];
      this.apiExpectations = [];
      this.liveApiProbeResults = [];
      this.loading = false;
      return;
    }

    this.loading = true;

    const requests: {
      inspectedUser: Observable<CurrentUser | null>;
      liveApiProbeResults: Observable<ApiProbeResult[]>;
    } = {
      inspectedUser: this.loadInspectedUser(),
      liveApiProbeResults: includeLiveProbes ? this.runLiveApiProbes(this.sessionUser) : of(this.liveApiProbeResults)
    };

    forkJoin(requests).subscribe({
      next: ({ inspectedUser, liveApiProbeResults }) => {
        this.inspectedUser = inspectedUser;
        this.inspectedUserJson = inspectedUser ? JSON.stringify(inspectedUser, null, 2) : '';
        this.routeProbes = this.routeProbeDefinitions.map((probe) => ({
          ...probe,
          allowed: this.hasPermissionsForUser(inspectedUser, probe.permissions)
        }));
        this.apiExpectations = this.apiProbeDefinitions.map((probe) => ({
          ...probe,
          allowed: this.hasPermissionsForUser(inspectedUser, probe.permissions)
        }));
        this.liveApiProbeResults = liveApiProbeResults;
        this.lastUpdated = new Date().toLocaleString();
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load the selected user authorization context.';
        this.loading = false;
      }
    });
  }

  private loadInspectedUser(): Observable<CurrentUser | null> {
    if (!this.sessionUser) {
      return of(null);
    }

    const requestedUserId = this.resolveSelectedUserId(this.sessionUser, this.inspectableUsers);

    if (!this.canInspectOtherUsers || requestedUserId === this.sessionUser.user_id) {
      return of(this.sessionUser);
    }

    return this.http.get<CurrentUser>('/api/me.php', {
      params: {
        user_id: String(requestedUserId)
      }
    });
  }

  private resolveSelectedUserId(currentUser: CurrentUser | null, users: InspectableUserOption[]): number | null {
    if (!currentUser) {
      return null;
    }

    if (!this.canInspectOtherUsers) {
      return currentUser.user_id;
    }

    if (this.selectedUserId && users.some((user) => user.user_id === this.selectedUserId)) {
      return this.selectedUserId;
    }

    if (users.some((user) => user.user_id === currentUser.user_id)) {
      return currentUser.user_id;
    }

    return users[0]?.user_id ?? currentUser.user_id;
  }

  private runLiveApiProbes(sessionUser: CurrentUser | null): Observable<ApiProbeResult[]> {
    const requests = this.apiProbeDefinitions.map((probe) => this.runApiProbe(probe));
    if (!sessionUser) {
      return of([]);
    }
    return forkJoin(requests);
  }

  private runApiProbe(probe: ApiProbeDefinition): Observable<ApiProbeResult> {
    const expectedAllowed = this.hasPermissionsForUser(this.sessionUser, probe.permissions);

    return this.http.get(probe.url).pipe(
      map((response) => ({
        ...probe,
        expectedAllowed,
        status: 200,
        ok: true,
        bodyPreview: this.formatBodyPreview(response),
        error: null
      })),
      catchError((error) => of({
        ...probe,
        expectedAllowed,
        status: error.status ?? null,
        ok: false,
        bodyPreview: this.formatBodyPreview(error.error),
        error: error.message ?? 'Request failed'
      }))
    );
  }

  private hasPermissionsForUser(user: CurrentUser | null, permissions: AppPermission[]): boolean {
    if (!user) {
      return false;
    }

    if (permissions.length === 0) {
      return true;
    }

    return permissions.every((permission) => user.permissions.includes(permission));
  }

  private buildTokenSummary(label: string, token: string | null): TokenSummary {
    if (!token) {
      return this.getEmptyTokenSummary(label);
    }

    try {
      const payload = this.parseJwtPayload(token);
      const issuedAt = typeof payload?.iat === 'number' ? this.formatUnixTime(payload.iat) : null;
      const expiresAt = typeof payload?.exp === 'number' ? this.formatUnixTime(payload.exp) : null;
      const secondsUntilExpiry = typeof payload?.exp === 'number'
        ? payload.exp - Math.floor(Date.now() / 1000)
        : null;

      return {
        label,
        present: true,
        issuedAt,
        expiresAt,
        secondsUntilExpiry,
        userId: typeof payload?.user_id === 'number' ? payload.user_id : null,
        error: null
      };
    }
    catch (error) {
      return {
        label,
        present: true,
        issuedAt: null,
        expiresAt: null,
        secondsUntilExpiry: null,
        userId: null,
        error: error instanceof Error ? error.message : 'Unable to decode token'
      };
    }
  }

  private getEmptyTokenSummary(label: string): TokenSummary {
    return {
      label,
      present: false,
      issuedAt: null,
      expiresAt: null,
      secondsUntilExpiry: null,
      userId: null,
      error: null
    };
  }

  private parseJwtPayload(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Token is not a valid JWT');
    }

    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');

    return JSON.parse(atob(padded));
  }

  private formatUnixTime(unixTime: number): string {
    return new Date(unixTime * 1000).toLocaleString();
  }

  private formatBodyPreview(body: unknown): string {
    if (body === null || body === undefined) {
      return '';
    }

    if (typeof body === 'string') {
      return body.length > 400 ? `${body.slice(0, 400)}...` : body;
    }

    const json = JSON.stringify(body, null, 2);
    return json.length > 400 ? `${json.slice(0, 400)}...` : json;
  }
}
